import { StudentAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/StudentAssignmentCubit'
import {
  AssignmentState,
  LibraryObjectState,
} from '@breakoutlearning/firebase-repository/types'
import { PageTitle } from 'components/PageTitle'
import { CircleAlert } from 'components/icons/CircleAlert'
import { BreakoutAlert } from 'components/design-system/BreakoutAlert'
import { formatDateTime } from 'util/dates'
import { XSmallIcon } from 'components/icons/XSmall'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import {
  StudentAssignmentCubitProvider,
  useStudentAssignmentCubit,
} from 'hooks/cubits/studentAssignment'
import { useDialogs } from 'hooks/dialogs'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { useLTIMode } from 'hooks/use-lti-mode'
import { useTranslationTyped } from 'i18n/i18n'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { StudentAssignmentEnroll } from './StudentAssignmentEnrollment'
import { StudentAssignmentHeader } from './StudentAssignmentHeader'
import {
  StudentAssignmentInfo,
  StudentAssignmentInfoLoading,
} from './StudentAssignmentInfo'
import { StudentAssignmentLibraryActionParser } from './StudentAssignmentLibraryActionParser'
import {
  StudentAssignmentLoadingPage,
  StudentAssignmentPendingPage,
} from './StudentAssignmentPendingPage'
import {
  StudentAssignmentSteps,
  StudentAssignmentStepsLoading,
} from './StudentAssignmentSteps'
import { StudentAssignmentJoinSessionDialog } from './dialogs/JoinSessionDialog'

export const StudentAssignmentPage = observer(function StudentAssignmentPage({
  assignmentId,
  sectionId,
  roomStateId,
}: {
  assignmentId: string
  sectionId: string
  roomStateId: string | undefined
}) {
  const { showDialog, dialogStore } = useDialogs()
  const store = useRootStore()
  const repository = useRepository()
  const { ltiMode } = useLTIMode()
  const { tt } = useTranslationTyped()

  const cubit = useCubitBuilder(
    () =>
      new StudentAssignmentCubit(repository, {
        sectionId,
        assignmentId,
        roomStateId,
        onSectionFetchPermissionError: () => {
          store.navigateTo('home')
          const className = cubit.section.data.className
          let body =
            tt.student_assignment.toast_unnamed_section_fetch_permission_error()
          if (className) {
            body = tt.student_assignment.toast_section_fetch_permission_error({
              className,
            })
          }
          toast.error(body, { duration: 8 * 1000 })
        },
      }),
    [repository]
  )

  useEffect(() => {
    // if the roomStateId changes, change the url
    return reaction(
      // isSolo reads a default until the assignment doc arrives, so the
      // room-id change must wait for it before deciding whether to navigate
      () => ({
        roomStateId: cubit.roomStateId,
        assignmentLoaded: cubit.assignment.isLoaded,
      }),
      ({ roomStateId, assignmentLoaded }) => {
        if (!assignmentLoaded) return
        // solo rooms are an implementation detail: keep the plain assignment
        // URL, because switching routes remounts the page and disposes the
        // cubit that an open quiz dialog is still bound to
        if (cubit.assignment.isSolo) return
        // already in sync: navigating to the same URL would only steal focus
        // (navigateTo resets it to the top anchor)
        const routerRoomStateId =
          store.router.params?.roomStateId?.toString() || undefined
        if (roomStateId === routerRoomStateId) return
        const route = roomStateId ? 'assignmentWithRoom' : 'assignment'
        store.navigateTo(route, {
          assignmentId: cubit.assignmentId,
          sectionId: cubit.sectionId,
          roomStateId: roomStateId,
        })
      }
    )
  }, [cubit, store])

  useEffect(() => {
    const disposeJoinDialogReaction = reaction(
      () => ({
        canPop: cubit.canPopJoinDialog && dialogStore.dialogs.length === 0,
      }),
      ({ canPop }, prev) => {
        if (!canPop || prev?.canPop === canPop) return
        showDialog(
          ({ remove }) => (
            <StudentAssignmentJoinSessionDialog
              cubit={cubit}
              closeSelf={remove}
            />
          ),
          {
            namespace: 'join-session',
            ignoreOnNamespace: true,
          }
        )
      },
      { fireImmediately: true }
    )
    return () => {
      disposeJoinDialogReaction()
    }
  }, [dialogStore, cubit, showDialog])

  if (ltiMode) {
    if (!cubit.isReady) return <StudentAssignmentLoadingPage />
    if (
      cubit.assignment.data.assignmentState === AssignmentState.unconfigured
    ) {
      return <StudentAssignmentPendingPage />
    }
  }

  return (
    <StudentAssignmentCubitProvider value={cubit}>
      <PageTitle title={cubit.assignment.slideDeck.data.slideDeckName} />
      <div
        className="z-50 h-full p-5 md:p-[2.5rem]"
        data-testid="student-assignment-page"
      >
        <div className="items-left flex h-full flex-col align-middle">
          <StudentAssignmentHeader />
          <NotYetAvailableNotice />
          <div className="mt-2 grid flex-grow rounded-3xl bg-surface p-5 md:overflow-auto lg:overflow-hidden">
            <div className="grid h-full max-h-full grid-cols-1 grid-rows-2 flex-col-reverse items-center lg:grid-cols-2 lg:grid-rows-1 lg:overflow-hidden">
              <StudentAssignmentView />
            </div>
          </div>
        </div>
      </div>
      <StudentAssignmentLibraryActionParser />
    </StudentAssignmentCubitProvider>
  )
})

const NotYetAvailableNotice = observer(function NotYetAvailableNotice() {
  const cubit = useStudentAssignmentCubit()
  const { tt } = useTranslationTyped()
  const inFuture = cubit.assignedAtIsInFutureForCurrentUser
  // The gate above honors the student's individual availableFrom, so the
  // displayed date must too — the base assignedAtDate can already be in the
  // past for a student whose override opens later.
  const assignedAtDate = cubit.effectiveAssignedAtDate

  if (!inFuture || !assignedAtDate) return null

  return (
    <div data-testid="assignment-not-yet-available-notice" className="mt-2">
      <BreakoutAlert variant="warning">
        {tt.student_assignment.not_yet_available_notice({
          date: formatDateTime(assignedAtDate),
        })}
      </BreakoutAlert>
    </div>
  )
})

const StudentAssignmentView = observer(function StudentAssignmentView() {
  const {
    isReady,
    libraryObject: { requiresPayment, libraryObjectState },
  } = useStudentAssignmentCubit()

  const isExpired = libraryObjectState === LibraryObjectState.expired

  const isCanceled = libraryObjectState === LibraryObjectState.canceled

  const AssignmentInfoView = isReady ? (
    <StudentAssignmentInfo />
  ) : (
    <StudentAssignmentInfoLoading />
  )

  const AssignmentMainView = useMemo(() => {
    if (!isReady) return <StudentAssignmentStepsLoading />
    if (isCanceled) return <CanceledView />
    if (requiresPayment) return <StudentAssignmentEnroll />
    if (isExpired) return <ExpiredView />
    return <StudentAssignmentSteps />
  }, [isReady, requiresPayment, isExpired, isCanceled])

  return (
    <>
      {AssignmentInfoView}
      {AssignmentMainView}
    </>
  )
})

const ExpiredView = observer(function ExpiredView() {
  const { t } = useTranslation()

  return (
    <div
      data-testid="assignment-expired-view"
      className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl bg-core-tertiary px-9 py-5"
    >
      <CircleAlert size={20} className="stroke-breakout-red" />
      <h2
        data-testid="assignment-expired-heading"
        className="text-title-medium"
      >
        {t('student_assignment.assignment_expired')}
      </h2>
      <p
        data-testid="assignment-expired-description"
        className="max-w-[350px] text-body-medium"
      >
        {t('student_assignment.assignment_expired_description')}
      </p>
    </div>
  )
})

const CanceledView = observer(function CanceledView() {
  const { t } = useTranslation()
  const user = useBreakoutUser()
  const cubit = useStudentAssignmentCubit()

  const tokensAvailable = user.availableTokens

  // todo(ashold12): modified copy for section pass assignments
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-3xl bg-core-tertiary px-9 py-5 text-center">
      <XSmallIcon size={30} className="stroke-core-error" />
      <div className="flex max-w-[366px] flex-col gap-1">
        <h2 className="text-title-large">
          {t('student_assignment.assignment_canceled')}
        </h2>
        {!cubit.section.usesSectionPass && (
          <p className="max-w-[350px] text-body-medium">
            {t('student_assignment.assignment_canceled_description')}
          </p>
        )}
      </div>
      {!cubit.section.usesSectionPass && (
        <div className="flex items-center">
          <p className="text-body-medium">
            {t('student_assignment.tokens_available_title') + ' ='}&nbsp;
          </p>
          <strong className="text-title-small text-core-error">
            {t('student_library.token_number', { count: tokensAvailable })}
          </strong>
        </div>
      )}
    </div>
  )
})
